import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { adminHomeBannerAPI, uploadAPI } from '../services/api';
import './Users.css';

const defaultForm = {
  type: 'text',
  title: '',
  message: '',
  image_url: '',
  image_urls: [],
  background_color: '#F5F5F5',
  text_color: '#3D2031',
  is_active: false,
};

const HomeBanner = () => {
  const [formData, setFormData] = useState(defaultForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);

  const loadBanner = async () => {
    setLoading(true);
    try {
      const response = await adminHomeBannerAPI.get();
      const banner = response.data?.banner;
      if (banner) {
        let urls = [];
        if (Array.isArray(banner.image_urls) && banner.image_urls.length > 0) {
          urls = banner.image_urls.filter((u) => typeof u === 'string' && u.trim() !== '');
        } else if (banner.image_url) {
          urls = [banner.image_url];
        }
        setFormData({
          type: banner.type || 'text',
          title: banner.title || '',
          message: banner.message || '',
          image_url: banner.image_url || '',
          image_urls: urls,
          background_color: banner.background_color || '#F5F5F5',
          text_color: banner.text_color || '#3D2031',
          is_active: Boolean(banner.is_active),
        });
      }
    } catch (error) {
      toast.error('Error loading home banner: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBanner();
  }, []);

  const addSlideSlot = () => {
    setFormData((prev) => ({
      ...prev,
      image_urls: [...prev.image_urls, ''],
    }));
  };

  const removeSlideAt = (index) => {
    setFormData((prev) => ({
      ...prev,
      image_urls: prev.image_urls.filter((_, i) => i !== index),
    }));
  };

  const setSlideUrlAt = (index, url) => {
    setFormData((prev) => {
      const next = [...prev.image_urls];
      next[index] = url;
      return { ...prev, image_urls: next };
    });
  };

  const handleSlideUpload = async (index, file) => {
    if (!file) return;
    setUploadingIndex(index);
    try {
      const response = await uploadAPI.uploadImage(file);
      const imageUrl = response.data?.image_url;
      if (!imageUrl) throw new Error('No image URL returned');
      setSlideUrlAt(index, imageUrl);
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Upload failed: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploadingIndex(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const image_urls =
        formData.type === 'image'
          ? formData.image_urls.map((u) => (typeof u === 'string' ? u.trim() : '')).filter(Boolean)
          : [];
      if (formData.type === 'image' && image_urls.length === 0) {
        toast.error('Add at least one banner image (upload or paste URL)');
        setSaving(false);
        return;
      }
      const payload = {
        type: formData.type,
        title: formData.title,
        message: formData.message,
        background_color: formData.background_color,
        text_color: formData.text_color,
        is_active: formData.is_active,
        image_urls,
        image_url: image_urls[0] || '',
      };
      await adminHomeBannerAPI.update(payload);
      toast.success('Home banner updated');
      await loadBanner();
    } catch (error) {
      const msg =
        error.response?.data?.errors &&
        Object.values(error.response.data.errors).flat().join(' ');
      toast.error(msg || error.response?.data?.message || error.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading">Loading home banner...</div>;

  const slides =
    formData.type === 'image'
      ? formData.image_urls.length > 0
        ? formData.image_urls
        : ['']
      : [];

  return (
    <div className="users-container">
      <div className="table-container" style={{ padding: 20 }}>
        <h3 style={{ marginBottom: 16 }}>Initial Page Banner</h3>
        <p style={{ color: '#666', marginBottom: 20, maxWidth: 720 }}>
          For type <strong>Image</strong>, add one or more slides (carousel on the app home screen).
          Use eyebrow text in <strong>Title</strong> and headline in <strong>Message</strong> for overlay copy.
        </p>
        <form onSubmit={submit}>
          <div className="form-group">
            <label>Banner Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
            >
              <option value="text">Text</option>
              <option value="marquee">Animated Text (Marquee)</option>
              <option value="image">Image (one or more)</option>
            </select>
          </div>

          <div className="form-group">
            <label>Title (e.g. small caps line)</label>
            <input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              maxLength={150}
            />
          </div>

          <div className="form-group">
            <label>Message (e.g. main headline)</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              rows={3}
              maxLength={1000}
            />
          </div>

          {formData.type === 'image' && (
            <>
              <div className="form-group">
                <label>Banner images *</label>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ marginBottom: 12 }}
                  onClick={addSlideSlot}
                >
                  + Add slide
                </button>
                {slides.map((url, index) => (
                  <div
                    key={index}
                    style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 12,
                      background: '#fafafa',
                    }}
                  >
                    <div style={{ marginBottom: 8, fontWeight: 600 }}>Slide {index + 1}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-start' }}>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={uploadingIndex === index}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) void handleSlideUpload(index, f);
                          e.target.value = '';
                        }}
                      />
                      {uploadingIndex === index ? <span>Uploading…</span> : null}
                    </div>
                    <input
                      style={{ width: '100%', marginTop: 10 }}
                      placeholder="Image URL (or paste after upload)"
                      value={url}
                      onChange={(e) => setSlideUrlAt(index, e.target.value)}
                    />
                    {url ? (
                      <img
                        src={url}
                        alt=""
                        style={{
                          maxWidth: 280,
                          maxHeight: 140,
                          objectFit: 'cover',
                          marginTop: 10,
                          borderRadius: 6,
                        }}
                      />
                    ) : null}
                    <div style={{ marginTop: 8 }}>
                      <button type="button" className="btn btn-secondary" onClick={() => removeSlideAt(index)}>
                        Remove slide
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="form-group">
            <label>Background Color</label>
            <input
              value={formData.background_color}
              onChange={(e) => setFormData({ ...formData, background_color: e.target.value })}
              placeholder="#F5F5F5"
            />
          </div>

          <div className="form-group">
            <label>Text Color</label>
            <input
              value={formData.text_color}
              onChange={(e) => setFormData({ ...formData, text_color: e.target.value })}
              placeholder="#3D2031"
            />
          </div>

          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />{' '}
              Active
            </label>
          </div>

          <div className="form-actions">
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Saving...' : 'Save Banner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HomeBanner;
